import React, { useState, useEffect, useRef } from 'react';
import { API_URL, apiFetch } from '../config/api';

const activeGuideInstructions = {
    gyro: [
        'Ensure the drone is placed on a completely flat, stationary surface.',
        'Do not move, bump, or touch the vehicle during calibration.',
        'Keep clear of high-wind areas or vibrating machinery.',
        'The procedure completes autonomously in approximately 5 seconds.'
    ],
    accel: [
        'The autopilot will prompt you to place the drone in 6 successive orientations.',
        'Follow the live MAVLink logs in the wizard terminal.',
        'Orientations: Level, Left side, Right side, Nose up, Nose down, and Back side.',
        'Hold the vehicle completely still in each orientation until the beep confirms.'
    ],
    compass: [
        'Perform this test outdoors, away from large metal structures or power lines.',
        'When active, rotate the vehicle slowly 360° along all rotational axes (pitch, roll, yaw).',
        'Keep rotating until the live MAVLink logs report completion.',
        'Avoid carrying cell phones or keys near the vehicle during this test.'
    ],
    level: [
        'Ensure the vehicle is leveled perfectly on a flat ground reference surface.',
        'The autopilot registers this angle as a level pitch/roll state.',
        'Any misalignment here will cause the drone to drift during flight.',
        'Autonomous execution takes less than 3 seconds.'
    ],
    rc: [
        'Turn on your remote control transmitter before starting.',
        'Once active, deflect both control sticks fully to all four extreme corners.',
        'Toggle all mapped auxiliary switches and slider controls through their full range.',
        'Confirm stick deflection limits appear on the console log terminal.'
    ],
    esc: [
        '⚠️ CRITICAL: REMOVE ALL PROPELLERS FROM THE DRONE BEFORE PROCEEDING.',
        'Disconnect the drone battery, leaving MAVLink USB telemetry connected.',
        'Push the transmitter throttle stick to the maximum position, then boot the wizard.',
        'Plug in the drone battery. The motors will emit sync tones. Lower throttle fully.'
    ]
};

const activeGuideChecklist = {
    gyro: [
        { label: 'Drone is completely still', critical: false },
        { label: 'Surface is structurally stable', critical: false }
    ],
    accel: [
        { label: 'Secure hand-hold possible', critical: false },
        { label: 'Clear flat surfaces available nearby', critical: false }
    ],
    compass: [
        { label: 'Tested outdoors / away from metal', critical: false },
        { label: 'No magnetic materials on operator', critical: false }
    ],
    level: [
        { label: 'Ground is level (use a bubble level)', critical: false },
        { label: 'Drone legs/frame are fully seated flat', critical: false }
    ],
    rc: [
        { label: 'RC transmitter turned ON', critical: false },
        { label: 'Sticks are clear to move freely', critical: false }
    ],
    esc: [
        { label: '⚠️ ALL PROPELLERS REMOVED', critical: true },
        { label: 'Battery initially disconnected', critical: false }
    ]
};

function CalibrationWizard({ droneId = null, telemetry = {}, onClose = null, initialType = null }) {
    const [calibType, setCalibType] = useState(initialType); // 'gyro' | 'accel' | 'compass' | 'level' | null
    const [status, setStatus] = useState(initialType ? 'running' : 'idle'); // 'idle' | 'running' | 'success' | 'failed'
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [progress, setProgress] = useState(0);

    const prevMessageRef = useRef('');
    const speechEnabled = true;

    const connected = telemetry.connected || false;
    const armed = telemetry.armed || false;
    const activeMsg = telemetry.calibration_message || '';
    const activePct = telemetry.calibration_percent || 0;
    const calibActive = telemetry.calibration_active || false;

    // Text to Speech announcement helper
    const speakInstruction = (text) => {
        if (!speechEnabled || !window.speechSynthesis) return;
        try {
            window.speechSynthesis.cancel(); // Cancel ongoing speakings
            const cleanText = text.replace(/[^a-zA-Z0-9\s:%,.]/g, ''); // strip MAVLink tags
            const utterance = new SpeechSynthesisUtterance(cleanText);
            utterance.rate = 0.95;
            utterance.pitch = 1.0;
            window.speechSynthesis.speak(utterance);
        } catch (e) {
            console.error('Speech synthesis error:', e);
        }
    };

    // Synchronize MAVLink feedback from telemetry channel
    useEffect(() => {
        if (calibActive && activeMsg) {
            setStatus('running');
            setProgress(activePct);
            
            // Check if log message is new to avoid spam
            if (activeMsg !== prevMessageRef.current) {
                prevMessageRef.current = activeMsg;
                setLogs(prev => [...prev.slice(-30), `[${new Date().toLocaleTimeString()}] ${activeMsg}`]);
                
                // Read instruction out loud
                speakInstruction(activeMsg);

                // Handle automated terminal cases
                const upper = activeMsg.toUpperCase();
                if (upper.includes('SUCCESS') || upper.includes('PASSED') || upper.includes('COMPLETED')) {
                    setStatus('success');
                    setProgress(100);
                    speakInstruction('Sensor calibration completed successfully!');
                } else if (upper.includes('FAIL') || upper.includes('ERR') || upper.includes('ABORT')) {
                    setStatus('failed');
                    speakInstruction('Calibration failed or was aborted by the autopilot.');
                }
            }
        } else if (!calibActive && status === 'running') {
            // Auto complete if not active anymore and was running
            setStatus('idle');
            setCalibType(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMsg, activePct, calibActive]);

    const startCalibration = async (type) => {
        if (armed) {
            setFeedbackMsg('❌ Critical Lock: Cannot calibrate while drone is ARMED!');
            return;
        }

        setLoading(true);
        setFeedbackMsg('');
        setStatus('running');
        setCalibType(type);
        setProgress(5);
        setLogs([`[${new Date().toLocaleTimeString()}] Triggering ${type.toUpperCase()} calibration sequence...`]);
        
        speakInstruction(`Initiating remote ${type} calibration. Please wait.`);

        try {
            const res = await apiFetch(`${API_URL}/calibrate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ droneId, type })
            });

            const data = await res.json();
            if (data.success) {
                setFeedbackMsg(`🚀 Command acknowledged. Waiting for autopilot feedback...`);
            } else {
                setStatus('failed');
                setFeedbackMsg(`❌ Failed: ${data.message}`);
                speakInstruction('Failed to trigger calibration.');
            }
        } catch (err) {
            setStatus('failed');
            setFeedbackMsg(`❌ Error: ${err.message}`);
            speakInstruction('Error triggering remote calibration.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialType) {
            startCalibration(initialType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialType]);

    const cancelCalibration = () => {
        setStatus('idle');
        setCalibType(null);
        setFeedbackMsg('Calibration aborted by operator.');
        speakInstruction('Calibration aborted.');
        if (window.speechSynthesis) window.speechSynthesis.cancel();
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(5, 8, 16, 0.85)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            fontFamily: "'Outfit', 'Inter', sans-serif"
        }}>
            <div style={{
                background: 'rgba(17, 24, 39, 0.75)',
                border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                borderRadius: '24px',
                width: '100%',
                maxWidth: status !== 'idle' ? '920px' : '650px',
                padding: '28px',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
                color: 'var(--textPrimary)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'max-width 0.4s ease'
            }}>
                {/* Visual Glowmorphism glowing orb decoration */}
                <div style={{
                    position: 'absolute',
                    top: '-60px',
                    right: '-60px',
                    width: '150px',
                    height: '150px',
                    background: status === 'running' ? 'rgba(59, 130, 246, 0.15)' : (status === 'success' ? 'color-mix(in srgb, var(--success) 15%, transparent)' : 'color-mix(in srgb, var(--danger) 8%, transparent)'),
                    filter: 'blur(60px)',
                    borderRadius: '50%',
                    zIndex: 0,
                    transition: 'all 0.5s ease'
                }} />

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>📡</span>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, letterSpacing: '0.5px' }}>
                                REMOTE CALIBRATION WIZARD
                            </h2>
                            <span style={{ fontSize: '11px', color: 'var(--textSecondary)', textTransform: 'uppercase' }}>
                                Drone ID: <strong style={{ color: 'var(--accent)' }}>{droneId}</strong>
                            </span>
                        </div>
                    </div>
                    {onClose && status !== 'running' && (
                        <button 
                            onClick={onClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--textMuted)',
                                fontSize: '20px',
                                cursor: 'pointer',
                                padding: '5px',
                                transition: 'color 0.2s'
                            }}
                            onMouseEnter={e => e.target.style.color = 'var(--textPrimary)'}
                            onMouseLeave={e => e.target.style.color = 'var(--textMuted)'}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Warning Alert if Armed */}
                {armed && (
                    <div style={{
                        padding: '12px 16px',
                        background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                        border: '1px solid var(--danger)',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: 'var(--danger)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        zIndex: 1
                    }}>
                        <span>⚠️</span>
                        <span>SAFETY LOCK ACTIVE: You cannot trigger sensor calibration while the vehicle is armed. Disarm first.</span>
                    </div>
                )}

                {/* Dashboard Selection (Idle State) */}
                {status === 'idle' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', zIndex: 1 }}>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--textSecondary)', lineHeight: 1.5 }}>
                            Select the sensor you want to configure. Ensure the vehicle is placed stationary on a level surface. Gyroscope and Horizon calibrations are fully autonomous and require no hands-on vehicle movement.
                        </p>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '14px',
                            marginTop: '10px'
                        }}>
                            {/* Gyroscope */}
                            <button
                                onClick={() => startCalibration('gyro')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>🎛️</span>
                                <strong style={{ fontSize: '13px' }}>Gyroscope</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Static zero-rate offsets calibration. Hands-off.</span>
                            </button>

                            {/* Accelerometer */}
                            <button
                                onClick={() => startCalibration('accel')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>📐</span>
                                <strong style={{ fontSize: '13px' }}>Accelerometer</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Gravity vectors and scaling. 3D orientation.</span>
                            </button>

                            {/* Compass */}
                            <button
                                onClick={() => startCalibration('compass')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>🧭</span>
                                <strong style={{ fontSize: '13px' }}>Magnetometer (Compass)</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Hard/soft iron offsets correction. Axis rotations.</span>
                            </button>

                            {/* Level Horizon */}
                            <button
                                onClick={() => startCalibration('level')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>⚖️</span>
                                <strong style={{ fontSize: '13px' }}>Level Horizon</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Board alignment reference calibration. Flat surface.</span>
                            </button>

                            {/* Radio Control */}
                            <button
                                onClick={() => startCalibration('rc')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>🎮</span>
                                <strong style={{ fontSize: '13px' }}>Radio Control</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Stick range mapping. Move sticks to extreme corners.</span>
                            </button>

                            {/* ESC Calibration */}
                            <button
                                onClick={() => startCalibration('esc')}
                                disabled={armed || !connected || loading}
                                style={{
                                    padding: '16px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    borderRadius: '16px',
                                    color: 'var(--textPrimary)',
                                    cursor: armed || !connected || loading ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => { if(!armed && connected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                            >
                                <span style={{ fontSize: '20px' }}>⚡</span>
                                <strong style={{ fontSize: '13px' }}>ESC Calibration</strong>
                                <span style={{ fontSize: '10px', color: 'var(--textSecondary)' }}>Motor timing sync. REMOVE PROPELLERS BEFORE RUNNING!</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Active Calibration Execution screen */}
                {status !== 'idle' && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 1fr',
                        gap: '20px',
                        zIndex: 1,
                        minHeight: '380px'
                    }}>
                        {/* Left Side: Console & Progress */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
                            <div style={{
                                padding: '12px 16px',
                                background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)',
                                border: '1px solid color-mix(in srgb, var(--textPrimary) 5%, transparent)',
                                borderRadius: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{
                                        fontSize: '11px', 
                                        fontWeight: 700, 
                                        color: status === 'success' ? 'var(--success)' : (status === 'failed' ? 'var(--danger)' : 'var(--accent)'),
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px'
                                    }}>
                                        ● {calibType?.toUpperCase()} CALIBRATION ACTIVE
                                    </span>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{progress}%</span>
                                </div>

                                {/* Progress bar */}
                                <div style={{ background: 'color-mix(in srgb, var(--textPrimary) 8%, transparent)', height: '6px', borderRadius: '3px', width: '100%', overflow: 'hidden' }}>
                                    <div style={{ 
                                        width: `${progress}%`, 
                                        height: '100%', 
                                        background: status === 'success' ? 'var(--success)' : (status === 'failed' ? 'var(--danger)' : 'linear-gradient(90deg, var(--accent) 0%, var(--accentSecondary) 100%)'),
                                        borderRadius: '3px',
                                        transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }} />
                                </div>
                            </div>

                            {/* Autopilot LOG Console */}
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                <span style={{ fontSize: '11px', color: 'var(--textSecondary)', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                                    Autopilot Live Logs (STATUSTEXT Feedback)
                                </span>
                                <div style={{
                                    background: '#0a0d14',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 5%, transparent)',
                                    borderRadius: '14px',
                                    padding: '12px 14px',
                                    flex: 1,
                                    height: '240px',
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    fontFamily: 'Consolas, monospace',
                                    fontSize: '11px',
                                    color: '#a0aec0'
                                }}>
                                    {logs.length === 0 ? (
                                        <div style={{ color: '#4a5568', fontStyle: 'italic' }}>Waiting for autopilot feedback...</div>
                                    ) : (
                                        logs.map((log, idx) => {
                                            let logColor = '#a0aec0';
                                            if (log.includes('SUCCESS') || log.includes('Success')) logColor = 'var(--success)';
                                            else if (log.includes('FAIL') || log.includes('Fail') || log.includes('ERR')) logColor = 'var(--danger)';
                                            else if (log.includes('Log:')) logColor = '#3b82f6';

                                            return (
                                                <div key={idx} style={{ color: logColor }}>
                                                    {log}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Active Step-by-Step Instructions & Safety Panel */}
                        <div style={{
                            background: calibType === 'esc' ? 'color-mix(in srgb, var(--danger) 3%, transparent)' : 'rgba(34, 211, 238, 0.02)',
                            border: `1px solid ${calibType === 'esc' ? 'color-mix(in srgb, var(--danger) 15%, transparent)' : 'rgba(34, 211, 238, 0.1)'}`,
                            borderRadius: '16px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            minWidth: 0,
                            overflowY: 'auto',
                            maxHeight: '380px'
                        }} className="custom-scrollbar">
                            
                            {/* Guide Title */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)', paddingBottom: '8px' }}>
                                <span style={{ fontSize: '20px' }}>
                                    {calibType === 'gyro' ? '🎛️' : calibType === 'accel' ? '📐' : calibType === 'compass' ? '🧭' : calibType === 'level' ? '⚖️' : calibType === 'rc' ? '🎮' : '⚡'}
                                </span>
                                <div>
                                    <strong style={{ fontSize: '13px', display: 'block', color: 'var(--textPrimary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {calibType === 'gyro' ? 'Gyroscope Zero-Rate Guide' : 
                                         calibType === 'accel' ? 'Accelerometer 3D Vector Guide' : 
                                         calibType === 'compass' ? 'Magnetometer (Compass) Guide' : 
                                         calibType === 'level' ? 'Horizon Board Alignment Guide' : 
                                         calibType === 'rc' ? 'Radio Stick Range Mapping' : 
                                         'ESC Throttle Sync Guide'}
                                    </strong>
                                    <span style={{ fontSize: '9px', color: 'var(--textSecondary)', textTransform: 'uppercase' }}>
                                        Live Crew Checklist
                                    </span>
                                </div>
                            </div>

                            {/* Steps List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase' }}>
                                    📋 Calibration Steps:
                                </span>
                                {(activeGuideInstructions[calibType] || []).map((step, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: '6px', fontSize: '11px', lineHeight: '1.4', color: 'var(--textSecondary)' }}>
                                        <span style={{ color: calibType === 'esc' ? 'var(--danger)' : 'var(--accent)', fontWeight: 'bold' }}>{idx + 1}.</span>
                                        <span>{step}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Safety checklist block */}
                            <div style={{ marginTop: 'auto', paddingTop: '8px', borderTop: '1px dashed color-mix(in srgb, var(--textPrimary) 6%, transparent)' }}>
                                <span style={{
                                    fontSize: '10px', 
                                    fontWeight: 800, 
                                    color: calibType === 'esc' ? 'var(--danger)' : 'var(--success)', 
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    marginBottom: '4px'
                                }}>
                                    🛡️ Preflight Safety Checklist:
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {(activeGuideChecklist[calibType] || []).map((item, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '10.5px',
                                            color: item.critical ? 'var(--danger)' : 'var(--textSecondary)',
                                            fontWeight: item.critical ? 700 : 500
                                        }}>
                                            <span style={{ color: item.critical ? 'var(--danger)' : 'var(--success)' }}>✓</span>
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {/* Footer Controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', zIndex: 1 }}>
                    <div style={{ fontSize: '11px', color: 'var(--textSecondary)' }}>
                        {feedbackMsg ? (
                            <span style={{ color: feedbackMsg.includes('❌') ? 'var(--danger)' : 'var(--success)' }}>
                                {feedbackMsg}
                            </span>
                        ) : (
                            <span>📢 Speech Synthesis: <strong style={{ color: 'var(--success)' }}>ONLINE</strong></span>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        {status === 'running' && (
                            <button
                                onClick={cancelCalibration}
                                style={{
                                    padding: '10px 18px',
                                    background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                                    color: 'var(--danger)',
                                    border: '1px solid var(--danger)',
                                    borderRadius: '10px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Cancel / Abort
                            </button>
                        )}
                        {(status === 'success' || status === 'failed') && (
                            <button
                                onClick={() => {
                                    setStatus('idle');
                                    setCalibType(null);
                                    setFeedbackMsg('');
                                }}
                                style={{
                                    padding: '10px 18px',
                                    background: 'var(--accent)',
                                    color: 'var(--textPrimary)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Done
                            </button>
                        )}
                        {status === 'idle' && onClose && (
                            <button
                                onClick={onClose}
                                style={{
                                    padding: '10px 18px',
                                    background: 'color-mix(in srgb, var(--textPrimary) 5%, transparent)',
                                    border: '1px solid color-mix(in srgb, var(--textPrimary) 10%, transparent)',
                                    borderRadius: '10px',
                                    color: 'var(--textPrimary)',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Close Dashboard
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Embedded styles for pulse animation */}
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}} />
        </div>
    );
}

export default CalibrationWizard;
